/**
 * EvidenceActions — the Evidence named primary actions
 * (frontend-entity-coverage §2 / §6): **set polarity / weight** (`PATCH
 * polarity` / `weight`, `[EXISTS]` for polarity, `02 §3.4`) and **accept /
 * reject** the evidence relation (`PATCH evidence.status`,
 * candidate→accepted/needs_review/rejected, `03 §7`, `[PROPOSED]` P1).
 *
 * Set-polarity opens a small dialog to pick a polarity stance and a weight,
 * then PATCHes the row. Accept/Reject set `status`; because `evidence.status`
 * is `[PROPOSED]`, those writes **degrade, never crash** — a failed PATCH only
 * raises the standard error toast (via {@link useApiAction}). All writes
 * invalidate the evidence + claims caches so the ledger and badges refresh.
 */
import * as React from "react";
import { Check, Scale, X } from "@/components/ui/icon";

import {
  EVIDENCE_POLARITY,
  type Evidence,
  type EvidencePolarity,
} from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { humanize } from "@/lib/format";

import { useApiAction } from "../_shared/useApiAction";

export interface EvidenceActionsProps {
  evidence: Evidence;
}

/** Inline set-polarity/weight + accept/reject controls for an Evidence header. */
export function EvidenceActions({ evidence }: EvidenceActionsProps) {
  const { run, loading } = useApiAction();
  const [open, setOpen] = React.useState(false);
  const [polarity, setPolarity] = React.useState<EvidencePolarity>(
    evidence.polarity,
  );
  const [weight, setWeight] = React.useState<string>(
    evidence.weight != null ? String(evidence.weight) : "1",
  );

  const patch = async (body: Record<string, unknown>, message: string) => {
    try {
      await run(`/evidence/${encodeURIComponent(evidence.id)}`, {
        method: "PATCH",
        body,
        invalidateResources: ["evidence", "claims"],
        successMessage: message,
      });
    } catch {
      /* useApiAction surfaced the toast; degrade silently. */
    }
  };

  const savePolarity = async () => {
    const parsed = Number(weight);
    await patch(
      {
        polarity,
        weight: Number.isFinite(parsed) ? parsed : undefined,
      },
      "Evidence polarity updated.",
    );
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="evidence-set-polarity"
          >
            <Scale className="mr-2 h-4 w-4" />
            Set polarity / weight
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Set polarity &amp; weight</DialogTitle>
            <DialogDescription>
              How this evidence bears on its claim, and how heavily it counts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="evidence-polarity">Polarity</Label>
              <Select
                value={polarity}
                onValueChange={(v) => setPolarity(v as EvidencePolarity)}
              >
                <SelectTrigger id="evidence-polarity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVIDENCE_POLARITY.map((p) => (
                    <SelectItem key={p} value={p}>
                      {humanize(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="evidence-weight">Weight</Label>
              <Input
                id="evidence-weight"
                type="number"
                step="0.1"
                min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button disabled={loading} onClick={() => void savePolarity()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Button
        variant="outline"
        size="sm"
        disabled={loading}
        onClick={() => void patch({ status: "accepted" }, "Evidence accepted.")}
        data-testid="evidence-accept"
      >
        <Check className="mr-2 h-4 w-4" />
        Accept
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={loading}
        onClick={() => void patch({ status: "rejected" }, "Evidence rejected.")}
        data-testid="evidence-reject"
      >
        <X className="mr-2 h-4 w-4" />
        Reject
      </Button>
    </>
  );
}

export default EvidenceActions;
